import { useQuery } from '@tanstack/react-query';
import { Calculator, AlertTriangle, CheckCircle, DollarSign, Loader2, Users, TrendingDown, Percent } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

const fmtCurrency = (v: number | null | undefined) => {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : 0;
  return `$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

export default function CGTCalculatorPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['cgt-calculator'],
    queryFn: async () => { const r = await apiClient.get('/compliance/cgt'); return r.data.data; },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Scanning for capital gains events...</p>
        </div>
      </div>
    );
  }

  const summary = data?.summary;
  const clients = data?.clients || [];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Calculator}
        title="Capital Gains Tax Calculator"
        description="CGT event detection, discount eligibility, and small business concession analysis"
      />


      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Users className="h-5 w-5 text-primary" /></div>
                <div><p className="text-2xl font-bold">{summary.clientsWithGains}</p><p className="text-xs text-muted-foreground">Clients with CGT Events</p></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center"><DollarSign className="h-5 w-5 text-amber-500" /></div>
                <div><p className="text-2xl font-bold">{fmtCurrency(summary.totalGrossGains)}</p><p className="text-xs text-muted-foreground">Total Gross Gains</p></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center"><Percent className="h-5 w-5 text-green-500" /></div>
                <div><p className="text-2xl font-bold">{fmtCurrency(summary.totalDiscounts)}</p><p className="text-xs text-muted-foreground">Total Discounts Applied</p></div>
              </div>
            </CardContent>
          </Card>
          <Card className={summary.totalEstimatedCGT > 0 ? 'border-amber-500/30' : ''}>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center"><Calculator className="h-5 w-5 text-red-500" /></div>
                <div><p className="text-2xl font-bold">{fmtCurrency(summary.totalEstimatedCGT)}</p><p className="text-xs text-muted-foreground">Estimated CGT Payable</p></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Client Results */}
      {clients.filter((c: any) => c.gains && c.gains.length > 0).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No capital gains events detected across Xero-connected clients.
          </CardContent>
        </Card>
      ) : (
        clients.filter((c: any) => c.gains && c.gains.length > 0).map((c: any) => (
          <Card key={c.clientId}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">{c.clientName}</CardTitle>
                  <CardDescription>
                    {c.gains.length} CGT event{c.gains.length !== 1 ? 's' : ''} detected
                    {c.smallBusinessEligible && (
                      <Badge variant="secondary" className="ml-2 text-[10px]">SB Concessions Eligible</Badge>
                    )}
                  </CardDescription>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold">{fmtCurrency(c.estimatedCGT)}</p>
                  <p className="text-[10px] text-muted-foreground">Est. CGT payable</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Event/Account</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Gross Gain</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">50% Discount</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Net Gain</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Concessions</th>
                  </tr>
                </thead>
                <tbody>
                  {c.gains.map((g: any, i: number) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-2.5">
                        <span className="font-medium text-xs">{g.description}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">{fmtCurrency(g.grossGain)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-green-500">
                        {g.discountApplied ? `-${fmtCurrency(g.discountAmount)}` : '-'}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs font-medium">{fmtCurrency(g.netGain)}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {g.concessions && g.concessions.length > 0 ? (
                            g.concessions.map((con: string, j: number) => (
                              <Badge key={j} variant="outline" className="text-[9px]">{con}</Badge>
                            ))
                          ) : (
                            <span className="text-[10px] text-muted-foreground">None</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Summary row */}
              <div className="border-t bg-muted/20 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-muted-foreground">Total gross: <span className="font-mono font-medium text-foreground">{fmtCurrency(c.totalGrossGains)}</span></span>
                  <span className="text-muted-foreground">After discounts: <span className="font-mono font-medium text-foreground">{fmtCurrency(c.totalNetGains)}</span></span>
                </div>
                {c.recommendations && c.recommendations.length > 0 && (
                  <div className="flex items-center gap-1 text-xs text-amber-500">
                    <AlertTriangle className="h-3 w-3" />
                    {c.recommendations.length} recommendation{c.recommendations.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>

              {/* Recommendations */}
              {c.recommendations && c.recommendations.length > 0 && (
                <div className="border-t px-4 py-3">
                  <p className="text-xs font-medium mb-1.5 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-primary" /> Recommendations
                  </p>
                  <ul className="space-y-1">
                    {c.recommendations.map((r: string, i: number) => (
                      <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                        <span className="text-primary mt-0.5">-</span> {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {/* Clients with no CGT events */}
      {clients.filter((c: any) => !c.gains || c.gains.length === 0).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">No CGT Events Detected</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {clients.filter((c: any) => !c.gains || c.gains.length === 0).map((c: any) => (
                <Badge key={c.clientId} variant="outline" className="text-xs">{c.clientName}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
